import { useQuery } from "@apollo/client/react";
import { HelpCircle } from "lucide-react";
import { GET_FAQ } from "@/graphql/queries/support";

/**
 * „Dažni klausimai", from content/faq.yml — the same twelve entries the triage
 * model is given.
 *
 * That sharing is the point. The known causes used to live only inside the
 * prompt, so a correction reached the model and never the person; now answering
 * yourself and being answered by the model draw on one file. It is also what the
 * flow falls back to when no model answers, which is why it is a plain list of
 * text and nothing more — it has to be useful with the AI switched off.
 */

type Entry = { key: string; question: string; answer: string };
type Group = { category: string; label: string; entries: Entry[] };

export function SupportFaq({
  defaultOpen = false,
  bare = false,
}: {
  defaultOpen?: boolean;
  /** No outer fold — for /duk, where the FAQ IS the page. A page whose whole
   *  content sits behind a summary is a page with nothing on it. */
  bare?: boolean;
}) {
  const { data } = useQuery(GET_FAQ);
  const groups: Group[] = (data as any)?.faq ?? [];
  const total = groups.reduce((n, g) => n + g.entries.length, 0);

  if (total === 0) return null;

  const body = (
    <div className={bare ? "flex flex-col gap-6" : "flex flex-col gap-3 border-t px-3 py-3"}>
        {groups.map((group) => (
          <div key={group.category}>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {group.label}
            </p>
            <div className="flex flex-col gap-1.5">
              {group.entries.map((entry) => (
                /* Each answer folds on its own: twelve open answers is a wall,
                   and the question alone is what somebody scans. */
                <details key={entry.key} className="group" open={bare}>
                  <summary className="cursor-pointer list-none text-sm text-primary hover:underline">
                    {entry.question}
                  </summary>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {entry.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        ))}
    </div>
  );

  if (bare) return body;

  return (
    <details open={defaultOpen} className="rounded-lg border">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-sm font-medium">
        <HelpCircle className="h-4 w-4 text-primary" />
        Dažni klausimai
        <span className="text-muted-foreground">({total})</span>
      </summary>
      {body}
    </details>
  );
}
